import React, { useState, useEffect } from 'react';
import { Linkedin, Mail, Monitor, WifiOff, Languages, Accessibility, Volume2, Moon, Sun, Menu, X, AppWindow, BarChart3, Zap, Check } from 'lucide-react';
import Seo from './Seo.tsx';
import ContactForm from './ContactForm.tsx';

/**
 * Dedicated landing page for interactive experience development services.
 * Targets experiential marketing agencies working on brand activations,
 * trade shows, pop-ups, and live events. Cold outreach only.
 */
const ExperientialPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (stored === 'true' || stored === 'false') {
      setIsDarkMode(stored === 'true');
    } else {
      setIsDarkMode(mediaQuery.matches);
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    localStorage.setItem('darkMode', newValue.toString());
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className={`min-h-screen bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-sans ${isLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
      <Seo
        title="Ben Smith | Interactive Experience Development for Brand Activations & Live Events"
        description="Custom interactive software for brand activations, trade shows, pop-ups, and live events. Offline-first, real-time capable, fully accessible. 15+ years partnering with experiential agencies."
        path="/experiential"
        noindex
      />

      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md py-4 px-6 sticky top-0 shadow-soft z-50 border-b border-primary-100 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="/" className="text-2xl font-bold">
            <span className="text-neutral-900 dark:text-neutral-100">Ben Smith</span>
            <span className="text-primary-600 dark:text-primary-400"> Development</span>
          </a>
          <div className="hidden md:flex space-x-8 items-center">
            <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Home
            </a>
            <button onClick={() => scrollToSection('work')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Work
            </button>
            <button onClick={() => scrollToSection('capabilities')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Capabilities
            </button>
            <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              About
            </a>
            <button onClick={() => scrollToSection('contact')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Contact
            </button>
            <button
              onClick={toggleDarkMode}
              className="transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
          <button
            className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} className="text-neutral-700 dark:text-neutral-300" /> : <Menu size={24} className="text-neutral-700 dark:text-neutral-300" />}
          </button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-primary-100 dark:border-neutral-700 p-4 shadow-soft rounded-b-2xl z-50">
            <div className="flex flex-col space-y-4">
              <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Home</a>
              <button onClick={() => scrollToSection('work')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Work</button>
              <button onClick={() => scrollToSection('capabilities')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Capabilities</button>
              <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>About</a>
              <button onClick={() => scrollToSection('contact')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Contact</button>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section — Full-bleed installation photo */}
        <section className="relative text-white py-32 md:py-44 px-6 overflow-hidden">
          {/* Background image — UNI installation */}
          <img
            src="/portfolio-images/uni-installation.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay with gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/80 to-neutral-900/60"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 to-transparent"></div>

          <div className="relative max-w-5xl mx-auto z-10">
            <div className="inline-block mb-6 px-4 py-1.5 bg-accent-500/20 border border-accent-400/30 rounded-full">
              <span className="text-accent-300 text-sm font-medium tracking-wide uppercase">Experiential technology</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
              Interactive Installations<br />
              <span className="text-accent-400">for Live Experiences</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-2xl mb-6 text-neutral-200 leading-relaxed">
              Touchscreen activations. Real-time event displays. Multilingual visitor experiences. Built to run in the field, with or without WiFi.
            </p>
            <p className="text-lg max-w-2xl mb-10 text-neutral-400">
              I build the technical layer that experiential agencies need to bring interactive installations to life. You design. I make it work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection('work')} className="bg-accent-600 hover:bg-accent-500 text-white font-semibold py-3.5 px-8 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 text-center">
                See the work
              </button>
              <button onClick={() => scrollToSection('contact')} className="bg-transparent border-2 border-white/30 text-white font-semibold py-3.5 px-8 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-center">
                Start a conversation
              </button>
            </div>
          </div>
        </section>

        {/* Quick credibility bar */}
        <section className="bg-neutral-900 dark:bg-black border-b border-neutral-700 py-8 px-6">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16 text-center">
            <div>
              <div className="text-3xl font-bold text-accent-400">15+</div>
              <div className="text-sm text-neutral-400 mt-1">Years building interactive</div>
            </div>
            <div className="hidden md:block w-px h-10 bg-neutral-700"></div>
            <div>
              <div className="text-3xl font-bold text-accent-400">150+</div>
              <div className="text-sm text-neutral-400 mt-1">Projects delivered</div>
            </div>
            <div className="hidden md:block w-px h-10 bg-neutral-700"></div>
            <div>
              <div className="text-3xl font-bold text-accent-400">12</div>
              <div className="text-sm text-neutral-400 mt-1">Languages supported</div>
            </div>
            <div className="hidden md:block w-px h-10 bg-neutral-700"></div>
            <div>
              <div className="text-3xl font-bold text-accent-400">100%</div>
              <div className="text-sm text-neutral-400 mt-1">Offline capable</div>
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-neutral-900 dark:text-gray-100">
              Your agency designs the experience.<br />
              <span className="text-accent-600 dark:text-accent-400">I make it work in the real world.</span>
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed max-w-3xl mx-auto">
              Live events don't have stable WiFi. Trade show floors don't have IT departments. Audiences speak different languages and have different accessibility needs. These are the problems I build for — interactive software that runs reliably under real-world conditions, so your creative vision actually lands.
            </p>
          </div>
        </section>

        {/* Selected Work */}
        <section id="work" className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-neutral-900 dark:text-gray-100">
              Selected work
            </h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-16 max-w-2xl mx-auto">
              Interactive installations built for agencies across trade shows, public venues, and cultural events.
            </p>

            {/* Project 1: John Deere Trade Show */}
            <div className="mb-8 relative rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
              <img
                src="/portfolio-images/smartdetect-environment.jpg"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-[0.70] dark:opacity-[0.55] pointer-events-none"
              />
              <div className="relative p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-end">
                  <div className="lg:col-span-3">
                    <div className="bg-white/85 dark:bg-neutral-900/75 backdrop-blur-md rounded-xl p-4">
                      <img
                        src="/portfolio-images/smartdetect-ui.jpg"
                        alt="John Deere SmartDetect Trade Show Installation — three-panel interactive product showcase"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="bg-white/85 dark:bg-neutral-900/75 backdrop-blur-md rounded-xl p-6">
                      <div className="text-accent-600 dark:text-accent-400 text-sm font-medium tracking-wide uppercase mb-2">Trade Show Activation</div>
                      <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">John Deere — ConExpo 2026</h3>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-4 text-sm">
                        Interactive touchscreen installation for John Deere's flagship booth. Three-panel product showcase built for high-traffic trade show environments — native performance, touch-optimized, zero network dependency.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['Native App', 'Touch UI', 'Trade Show', 'Kiosk Mode', 'Multi-Display'].map((tag) => (
                          <span key={tag} className="bg-accent-100 dark:bg-accent-500/20 text-accent-700 dark:text-accent-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Project 2 & 3: Compact cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* UNI Anniversary */}
              <div className="rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
                <div className="relative h-52 overflow-hidden">
                  <img
                    src="/portfolio-images/uni-installation.jpg"
                    alt="Multi-station interactive installation at University of Northern Iowa"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="text-primary-300 text-xs font-medium tracking-wide uppercase">Event Installation · McCullough Creative</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">Multi-Station Interactive Experience</h3>
                  <p className="text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed mb-4">
                    Network of touchscreen stations for a major anniversary celebration. Offline-first architecture, remote CMS for non-technical staff, full accessibility suite with screen reader and adjustable display settings.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Electron', 'React', 'Offline-First', 'Touch UI', 'Accessible'].map((tag) => (
                      <span key={tag} className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Lincoln Home */}
              <div className="rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
                <div className="relative h-52 overflow-hidden">
                  <img
                    src="/portfolio-images/lincoln-home.jpg"
                    alt="Multilingual interactive visitor experience at Abraham Lincoln Home"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="text-accent-300 text-xs font-medium tracking-wide uppercase">Visitor Experience · National Park Service</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">12-Language Interactive Guided Experience</h3>
                  <p className="text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed mb-4">
                    Tablet-based visitor experience with AI-powered translation pipeline. Content updates auto-translate across all 12 languages with generated audio narration. Fully offline, zero ongoing translation costs.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['React', 'PWA', 'AI Translation', 'AI Audio', 'Multilingual'].map((tag) => (
                      <span key={tag} className="bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities — reordered for experiential relevance */}
        <section id="capabilities" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center text-neutral-900 dark:text-gray-100">
              What I build
            </h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-16 max-w-2xl mx-auto">
              What experiential agencies actually need on the floor.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <BarChart3 size={24} />,
                  title: 'Real-Time Event Dashboards',
                  description: 'Live interaction tracking, visitor flow analytics, and engagement metrics. Know what\'s working on the floor — not after the event.'
                },
                {
                  icon: <WifiOff size={24} />,
                  title: 'Offline-First Architecture',
                  description: 'Convention centers have terrible WiFi. Your installation won\'t care. Runs fully offline, syncs when connectivity returns.'
                },
                {
                  icon: <AppWindow size={24} />,
                  title: 'Cross-Platform & Native',
                  description: 'Native Windows apps for kiosk hardware. Electron for Mac. PWAs for tablets. React Native for mobile. Whatever the activation needs.'
                },
                {
                  icon: <Languages size={24} />,
                  title: 'Multilingual Systems',
                  description: 'AI-powered translation pipeline across text, audio, and video. Add a language in minutes, not weeks. Zero ongoing translation costs.'
                },
                {
                  icon: <Monitor size={24} />,
                  title: 'Remote CMS for Non-Technical Staff',
                  description: 'Your client\'s marketing team updates content through a simple admin panel. Changes push to all devices automatically.'
                },
                {
                  icon: <Accessibility size={24} />,
                  title: 'Full Accessibility',
                  description: 'WCAG-compliant with adjustable contrast, font sizing, dyslexia-friendly fonts, and custom screen readers. ADA compliance built in.'
                },
                {
                  icon: <Volume2 size={24} />,
                  title: 'AI-Powered Audio & Narration',
                  description: 'Auto-generated narration in any language, synced to on-screen content. Screen reader modes with real-time highlighting.'
                },
              ].map((capability, index) => (
                <div key={index} className="bg-white dark:bg-neutral-800 p-6 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="p-3 bg-accent-100 dark:bg-accent-900/40 rounded-xl w-fit mb-4 text-accent-600 dark:text-accent-400">
                    {capability.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-neutral-900 dark:text-neutral-100">{capability.title}</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How I Work / For Agencies */}
        <section className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-neutral-900 dark:text-gray-100">
              Built for agency partnerships
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed mb-12 max-w-3xl mx-auto">
              I don't compete with your team — I extend it. You own the client relationship, creative direction, and content. I handle the technical build. 15+ years partnering with agencies on 150+ projects for brands including John Deere, Cisco, Dell, and the National Park Service.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6">
                <div className="text-4xl font-bold text-accent-600 dark:text-accent-400 mb-2">01</div>
                <h3 className="font-bold text-lg mb-2 text-neutral-900 dark:text-neutral-100">You design</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">Your team owns the creative — visual design, content strategy, UX. I consult on what's technically possible and what's not.</p>
              </div>
              <div className="p-6">
                <div className="text-4xl font-bold text-accent-600 dark:text-accent-400 mb-2">02</div>
                <h3 className="font-bold text-lg mb-2 text-neutral-900 dark:text-neutral-100">I build</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">I develop the full technical layer — interactive software, CMS, accessibility, multilingual, offline architecture, hardware integration.</p>
              </div>
              <div className="p-6">
                <div className="text-4xl font-bold text-accent-600 dark:text-accent-400 mb-2">03</div>
                <h3 className="font-bold text-lg mb-2 text-neutral-900 dark:text-neutral-100">It just works</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">Day of event, it runs. No babysitting, no on-site developer needed. Your client's team manages content themselves.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 px-6 bg-neutral-900 dark:bg-black">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-white">Have an activation coming up?</h2>
            <p className="text-lg text-neutral-400 text-center mb-12 max-w-2xl mx-auto">
              Whether you're pitching a concept or have approved designs ready to build, I'd like to hear what you're working on.
            </p>
            <div className="bg-neutral-800 dark:bg-neutral-900 p-8 rounded-2xl shadow-soft max-w-2xl mx-auto border border-neutral-700">
              <ContactForm />
            </div>
            <div className="text-center mt-12">
              <p className="text-lg text-neutral-300">Or email me directly:</p>
              <a href="mailto:ben@bensmithdev.com" className="text-accent-400 font-semibold hover:text-accent-300 transition-colors duration-200">ben@bensmithdev.com</a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-6 md:mb-0">
                <a href="/" className="text-2xl font-bold">
                  <span className="text-white">Ben Smith</span>
                  <span className="text-accent-500"> Development</span>
                </a>
                <p className="mt-2 text-neutral-400">Interactive technology for brands and live experiences.</p>
              </div>
              <div className="flex space-x-6">
                <a
                  href="https://www.linkedin.com/in/benjaminchristophersmith/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-400 hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin size={24} />
                </a>
                <a
                  href="mailto:ben@bensmithdev.com"
                  className="text-neutral-400 hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="Email ben@bensmithdev.com"
                >
                  <Mail size={24} />
                </a>
              </div>
            </div>
            <div className="border-t border-neutral-800 mt-8 pt-8 text-center text-neutral-500">
              <p>&copy; {new Date().getFullYear()} Benjamin Smith. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default ExperientialPage;
